import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NewsletterManagementService } from '../newsletter-management.service';
import { CreateRegionRequestDTO } from '../interfaces/create-region-request';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { ActivatedRoute, Router } from '@angular/router';
import { FREQUENCY_NAME } from '../constants/frequency-friendly-names';
import {
  INewsletterBaseCategory,
  INewsletterCategory,
  NewsletterBaseCategory,
} from '../interfaces/i-newsletter-category';
import { AlertService } from 'src/app/shared/services/alert.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DeleteNewsletterRequest } from '../interfaces/delete-newsletter-request';
import { IUpdateNewsletterRequest } from '../interfaces/i-update-newsletter-request';
import { INewsletterCategoryGroup } from '../interfaces/i-newsletter-category-group';
import { Observable } from 'rxjs/internal/Observable';
import { forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTabsModule } from '@angular/material/tabs';
import { SectionHeaderComponent } from 'src/app/shared/standalone/components/section-header/section-header.component';
import { MatOption } from '@angular/material/core';
import { IUpdateNewsletterFormGroup } from '../interfaces/i-update-newsletter-form-group';
import { first, map, switchMap, take } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';

@Component({
  selector: 'app-edit-newsletter',
  templateUrl: './edit-newsletter.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatCheckboxModule,
    MatInputModule,
    MatListModule,
    MatTabsModule,
    MatRadioModule,
    MatDialogModule,
    MatButtonModule,
    SectionHeaderComponent,
  ],
})
export class EditNewsletterComponent implements OnInit {
  regionNames: string[] = [];
  intranetTemplates: string[] = [];
  newsletterForms: FormGroup<IUpdateNewsletterFormGroup>[] = [];
  submitted: boolean;
  updatedBy: string;
  availableFrequencies = Object.entries(FREQUENCY_NAME).map(([key, value]) => ({
    key: parseInt(key),
    name: value,
  }));
  lowerFrequencyAlternatives: INewsletterBaseCategory[][] = [];
  availableSites: CreateRegionRequestDTO[];
  focusSite: number;
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  constructor(
    private alertService: AlertService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private accountsService: CitywireInternalAccountsService,
    private newsletterManagementService: NewsletterManagementService
  ) {}

  ngOnInit(): void {
    const newsletterId = +this.route.snapshot.paramMap.get('id');
    this.getNewsletterById(newsletterId);

    this.accountsService.currentUser
      .pipe(
        take(1),
        map((user) => user.username.toLowerCase())
      )
      .subscribe((currentUserName) => (this.updatedBy = currentUserName));
  }

  getNewsletterById(newsletterId: number) {
    const newsletterById$ =
      this.newsletterManagementService.getNewsletterById$(newsletterId);
    const allNewsletters$ =
      this.newsletterManagementService.getAllNewsletters$();

    forkJoin([newsletterById$, allNewsletters$])
      .pipe(first())
      .subscribe(([newsletterRes, allSites]) => {
        newsletterRes.categoryByRegions.forEach(
          (newsletter, newsletterIndex: number) => {
            if (
              newsletter.region ===
              this.route.snapshot.queryParamMap.get('site')
            ) {
              this.focusSite = newsletterIndex;
            }

            this.initializeNewsletterForm(
              newsletter,
              allSites,
              newsletterIndex,
              newsletterRes.intranetTemplates
            );
          }
        );
      });
  }

  initializeNewsletterForm(
    newsletter: INewsletterCategory,
    allSites: INewsletterCategoryGroup[],
    newsletterIndex: number,
    intranetTemplates: string[]
  ) {
    const region = allSites.find((site) => site.region === newsletter.region);

    if (region) {
      this.regionNames.push(region.name);
      this.intranetTemplates = intranetTemplates;

      this.lowerFrequencyAlternatives[newsletterIndex] = region.categories
        .filter((cat) => cat.id !== newsletter.id)
        .map((cat) => new NewsletterBaseCategory(cat.id, cat.name));
    }

    this.newsletterForms.push(this.createNewsletterFormGroup(newsletter));
  }

  createNewsletterFormGroup(newsletter: INewsletterCategory): FormGroup {
    return this.fb.group({
      special: this.fb.control(newsletter.special ?? false),
      description: this.fb.control(newsletter.description),
      frequency: this.fb.control(newsletter.frequency, Validators.required),
      lowerFreqAlt: this.fb.control(newsletter.lowerFreqAlt ?? []),
      lowerFreqAltDefault: this.fb.control(newsletter.lowerFreqAltDefault ?? 0),
      name: this.fb.control(newsletter.name, Validators.required),
      imageUrl: this.fb.control(newsletter.imageUrl),
      displayOnce: this.fb.control(newsletter.displayOnce ?? false),
      default: this.fb.control(newsletter.default ?? false),
      regionsToUpdate: this.fb.control(
        [newsletter.region],
        Validators.required
      ),
      id: newsletter.id,
      updatedBy: this.updatedBy,
    });
  }

  updateNewsletter(
    newsletterIndex: number,
    selectedLowerFrequency: MatOption[]
  ) {
    const newsletterForm = this.newsletterForms[newsletterIndex];
    newsletterForm.patchValue({
      lowerFreqAlt: selectedLowerFrequency?.map((sel) => sel.value.id),
    });
    newsletterForm.markAllAsTouched();

    if (!newsletterForm.valid) {
      return;
    }

    this.submitted = true;
    const newsletterToUpdate: IUpdateNewsletterRequest =
      newsletterForm.getRawValue();

    this.newsletterManagementService
      .editNewsletter$(newsletterToUpdate)
      .pipe(first())
      .subscribe({
        next: () => this.onUpdateSuccess(newsletterToUpdate, newsletterIndex),
        error: () => this.onUpdateError(newsletterToUpdate),
        complete: () => (this.submitted = false),
      });
  }

  onUpdateSuccess(
    newsletterToUpdate: IUpdateNewsletterRequest,
    newsletterIndex: number
  ) {
    const regionName = this.regionNames[newsletterIndex];
    this.alertService.success(
      `Newsletter: "${newsletterToUpdate.name}" on "${regionName}" was updated successfully`
    );
    this.navigateToParentRouteWithQueryParam(
      newsletterToUpdate.regionsToUpdate[0]
    );
  }

  onUpdateError(newsletterToUpdate: IUpdateNewsletterRequest) {
    this.alertService.error(
      `Newsletter: "${newsletterToUpdate.name}" was not updated, please try again.`
    );
  }

  navigateToParentRouteWithQueryParam(site: string) {
    this.router.navigate(['./'], {
      relativeTo: this.route.parent,
      queryParams: { site },
    });
  }

  deleteNewsletter(newsletterIndex: number) {
    const newsletterToDelete =
      this.newsletterForms[newsletterIndex].getRawValue();

    const confirmationMessage = `Are you sure you want to delete "${newsletterToDelete.name}" from "${this.regionNames[newsletterIndex]}" site?`;

    this.openDeleteConfirmationDialog(confirmationMessage).subscribe(
      (result) => {
        if (!result) {
          return;
        }
        this.performDeleteAction(newsletterToDelete, result.includeTemplates);
      }
    );
  }

  openDeleteConfirmationDialog(
    message: string
  ): Observable<{ deleteNewsletter: boolean; includeTemplates: boolean }> {
    const dialogRef = this.dialog.open(this.modalTemplate, {
      data: {
        title: 'Delete newsletter',
        message: message,
      },
    });

    return dialogRef.afterClosed();
  }

  performDeleteAction(
    newsletterToDelete: IUpdateNewsletterRequest,
    includeIntranetTemplates: boolean
  ) {
    const deleteReq = new DeleteNewsletterRequest(
      newsletterToDelete.id,
      newsletterToDelete.regionsToUpdate,
      includeIntranetTemplates
    );

    this.newsletterManagementService
      .deleteNewsletter$(deleteReq)
      .subscribe(() => {
        this.navigateToParentRouteWithQueryParam(
          newsletterToDelete.regionsToUpdate[0]
        );
      });
  }
}
